import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Maximize2, Compass, Phone, MessageCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import { api, formatPrice, whatsappLink, telLink, PROPERTY_TYPES } from "@/lib/api";
import EnquiryForm from "@/components/EnquiryForm";
import SEO from "@/components/SEO";

export default function PropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [id]);

  if (p === null) return <div className="min-h-[60vh] flex items-center justify-center text-[#64748B]">Loading…</div>;
  if (p === false) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <h2 className="font-display text-2xl text-[#101827] mb-2">Property not found</h2>
        <Link to="/properties" className="ae-btn-primary mt-4"><ArrowLeft className="w-4 h-4" /> Browse properties</Link>
      </div>
    </div>
  );

  const images = p.images && p.images.length > 0 ? p.images : ["https://images.unsplash.com/photo-1773470920361-4f6cbb702ce0?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85"];
  const typeLabel = PROPERTY_TYPES.find((t) => t.value === p.property_type)?.label || p.property_type;
  const waMsg = `Hello Apurba Estate, I am interested in ${p.listing_id} — ${p.title}. Please share more details.`;

  return (
    <>
      <SEO
        title={`${p.title} — ${p.listing_id}`}
        description={`${p.title} in ${p.location}, ${p.district}. ${p.area_value} ${p.area_unit} · ${formatPrice(p.price)}. ${p.description?.slice(0, 150)}`}
        path={`/properties/${p.id}`}
        image={images[0]}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "RealEstateListing",
          name: p.title,
          description: p.description,
          image: images.slice(0, 4),
          offers: { "@type": "Offer", price: p.price, priceCurrency: "INR" },
          address: { "@type": "PostalAddress", addressLocality: p.location, addressRegion: p.district, addressCountry: "IN" },
        }}
      />
      <section className="bg-[#F4F6F8] pt-24 pb-6">
        <div className="ae-container">
          <Link to="/properties" className="text-sm text-[#64748B] hover:text-[#101827] inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back to properties</Link>
        </div>
      </section>

      <section className="pt-6 pb-10">
        <div className="ae-container grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="aspect-[16/10] rounded-xl overflow-hidden bg-[#F4F6F8] mb-3">
              <img src={images[idx]} alt={p.title} className="w-full h-full object-cover" />
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-3">
                {images.slice(0, 4).map((im, i) => (
                  <button key={i} onClick={() => setIdx(i)} className={`aspect-[4/3] rounded-lg overflow-hidden border-2 ${idx === i ? "border-[#183B66]" : "border-transparent"}`}>
                    <img src={im} alt={`Preview ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span className="ae-chip">{p.listing_id}</span>
              <span className="ae-chip">{typeLabel}</span>
              {p.is_demo && <span className="ae-chip bg-[#101827]/90 text-white !border-transparent">Demo Listing</span>}
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#101827] leading-tight">{p.title}</h1>
            <div className="flex items-center gap-2 text-[#64748B] mt-2"><MapPin className="w-4 h-4" /> {p.location}, {p.district}</div>

            <div className="mt-6 bg-[#101827] text-white rounded-xl p-5">
              <div className="ae-eyebrow mb-1">{p.price_label || "Asking Price"}</div>
              <div className="font-display text-3xl font-semibold text-[#C5A46D]">{formatPrice(p.price)}</div>
              {p.price_per_unit && <div className="text-sm text-white/60">₹ {p.price_per_unit}/{p.area_unit}</div>}
            </div>

            <div className="grid grid-cols-2 gap-3 mt-5">
              <Spec label="Area" value={`${p.area_value} ${p.area_unit}`} />
              <Spec label="Facing" value={p.facing || "—"} />
              {p.bedrooms && <Spec label="Bedrooms" value={p.bedrooms} />}
              {p.bathrooms && <Spec label="Bathrooms" value={p.bathrooms} />}
              <Spec label="Road Access" value={p.road_access || "—"} />
              <Spec label="Utilities" value={p.utilities || "—"} />
              <Spec label="Ownership" value={p.ownership_status} />
              <Spec label="District" value={p.district} />
            </div>

            <div className="flex gap-2 mt-6">
              <a href={telLink()} className="ae-btn-primary flex-1 justify-center" data-testid="prop-call-btn"><Phone className="w-4 h-4" /> Call Now</a>
              <a href={whatsappLink(waMsg)} target="_blank" rel="noopener noreferrer" className="ae-btn-whatsapp" data-testid="prop-wa-btn"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            </div>
          </div>
        </div>
      </section>

      <section className="ae-section pt-6">
        <div className="ae-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h3 className="font-display text-2xl font-semibold text-[#101827] mb-4">About this property</h3>
            <p className="text-[#1F2937] leading-relaxed">{p.description}</p>

            {p.highlights?.length > 0 && (
              <>
                <h3 className="font-display text-xl font-semibold text-[#101827] mt-10 mb-4">Highlights</h3>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {p.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#1F2937]"><CheckCircle2 className="w-5 h-5 text-[#059669] shrink-0 mt-0.5" /> {h}</li>
                  ))}
                </ul>
              </>
            )}

            {p.amenities?.length > 0 && (
              <>
                <h3 className="font-display text-xl font-semibold text-[#101827] mt-10 mb-4">Amenities</h3>
                <div className="flex flex-wrap gap-2">
                  {p.amenities.map((a, i) => <span key={i} className="ae-chip">{a}</span>)}
                </div>
              </>
            )}
          </div>
          <aside className="lg:col-span-5">
            <div className="ae-card p-6 sticky top-28">
              <EnquiryForm defaultService="property_enquiry" sourcePage={`property_${p.id}`} propertyId={p.id} title={`Enquire about ${p.listing_id}`} />
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

function Spec({ label, value }) {
  return (
    <div className="ae-card p-3">
      <div className="text-[11px] uppercase tracking-wider text-[#64748B]">{label}</div>
      <div className="font-semibold text-[#101827] text-sm mt-0.5">{value}</div>
    </div>
  );
}
